import { KBEntity } from '../knowledge-base.type';
import { useCallback, useEffect, useState } from 'react';

interface Options<Entity extends KBEntity> {
    getAction: (serviceUrl: string, parentKey?: string) => Promise<Entity[]>;
    serviceUrl: string;
}

export const useKbEntity = <Entity extends KBEntity>({
    getAction,
    serviceUrl,
}: Options<Entity>) => {
    const [items, setItems] = useState<Entity[]>([]);
    const [active, setActive] = useState<Entity>(null);
    const [isLoading, setIsLoading] = useState(false);

    const fetchItems = useCallback(async (parent?: KBEntity) => {
        setIsLoading(true);

        const actionFn = parent ? () => getAction(serviceUrl, parent.path) : getAction;

        const data = await actionFn(serviceUrl);
        setItems(data);
        setIsLoading(false);
    }, []);

    // Check if active item has been deleted (put to archive). In this case first item in the list becomes active
    useEffect(() => {
        if (active && !items.includes(active)) setActive(items[0] ?? null);
    }, [items]);

    const setActiveByEntity = useCallback((entity?: Entity) => setActive(entity || null), []);

    const setActiveById = useCallback(
        (id?: string) => {
            const activeCandidate = id && items.find((item) => item.id === id);
            setActive(activeCandidate);
        },
        [items]
    );

    const resetActive = useCallback(() => setActive(null), []);

    return {
        items,
        active,
        setActive: setActiveByEntity,
        setActiveById,
        isLoading,
        fetchItems,
        resetActive,
    };
};
